export type ProgressStatus = 'not_started' | 'in_progress' | 'completed';

export interface ProgressData {
  currentBlock: number | null;
  totalBlocks: number | null;
  progressPercentage: number;
}

/**
 * Simple state management for reading progress
 */
export class ProgressState {
  private readonly data: ProgressData = {
    currentBlock: null,
    totalBlocks: null,
    progressPercentage: 0,
  };

  private readonly subscribers = new Set<(state: ProgressData) => void>();
  private isActiveSession = false;

  constructor(totalBlocks: number) {
    this.data.totalBlocks = totalBlocks;
  }

  getData(): ProgressData {
    return { ...this.data };
  }

  isSessionActive(): boolean {
    return this.isActiveSession;
  }

  updateProgress(currentBlock: number): void {
    this.data.currentBlock = currentBlock;
    this.calculatePercentage();
    this.notifySubscribers();
  }

  // Load initial state from backend, ignoring backend status entirely
  loadFromBackend(currentBlock: number | null): void {
    this.data.currentBlock =
      currentBlock && currentBlock > 0 ? currentBlock : null;
    this.calculatePercentage();
    this.notifySubscribers();
  }

  markSessionActive(): void {
    this.isActiveSession = true;
  }

  subscribe(callback: (state: ProgressData) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // Private helpers
  private calculatePercentage(): void {
    if (this.data.totalBlocks && this.data.currentBlock) {
      this.data.progressPercentage = Math.round(
        (this.data.currentBlock / this.data.totalBlocks) * 100,
      );
    } else {
      this.data.progressPercentage = 0;
    }
  }

  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback(this.getData()));
  }
}
